import { toast } from "sonner";

// Admin-specific interfaces
interface DashboardStats {
  totalUsers: number;
  activeSubscriptions: number;
  monthlyUsage: number;
  growthRate: number;
  verifiedUsers?: number;
}

interface UsageMetrics {
  dailyRegistrations: number[];
  userEngagement: number;
  averageSessionDuration: number;
  // Additional metrics from backend
  totalActiveUsers?: number;
  // User activity patterns
  newUsersInPeriod?: number;
  activeUsersToday?: number;
  // Performance metrics
  userTrend?: "up" | "down" | "stable";
}

interface PlatformUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  userType: "individual" | "company";
  companyName?: string;
  siret?: string;
  subscriptionPlan: string;
  subscriptionStatus: "active" | "inactive" | "pending";
  subscriptionEndDate?: string;
  isEmailVerified: boolean;
  createdAt: string;
  updatedAt: string;
  customerId?: string; // Stripe customer ID
  subscriptionId?: string; // Stripe subscription ID
}

interface PlatformHealth {
  apiStatus: {
    status: "operational" | "degraded" | "down";
    responseTime: number;
    lastChecked: string;
  };
  database: {
    status: "connected" | "disconnected" | "error";
    responseTime: number;
    lastChecked: string;
  };
  emailService: {
    status: "active" | "inactive" | "degraded" | "error";
    responseTime: number;
    lastChecked: string;
  };
  stripeApi: {
    status: "active" | "inactive" | "degraded" | "error";
    responseTime: number;
    lastChecked: string;
  };
}

export interface AdminPlan {
  id: string;
  name: string;
  description: string;
  price: number;
  currency: string;
  billingInterval?: "month" | "year"; // Intervalle de facturation
  discountPercentage?: number | null; // Pourcentage de réduction (0-100)
  features: string[];
  enabledFeatures?: string[]; // Fonctionnalités activées pour ce plan
  maxUsers?: number | null; // null means unlimited
  isActive: boolean;
  isPopular: boolean;
  badge?: string;
  badgeColor?: string;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
  productId?: string; // Stripe product ID
  priceId?: string; // Stripe price ID
}

export interface AdminCreatePlanRequest {
  name: string;
  description: string;
  features: string[];
  billingInterval?: "month" | "year"; // Intervalle de facturation
  discountPercentage?: number | null; // Pourcentage de réduction (0-100)
  enabledFeatures?: string[]; // Fonctionnalités activées pour ce plan
  maxUsers?: number | null; // null means unlimited
  isActive: boolean;
  isPopular?: boolean;
  badge?: string;
  badgeColor?: string;
  displayOrder: number;
  productId?: string;
  priceId?: string;
}

// First admin creation interface
interface FirstAdminData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

interface FirstAdminResponse {
  message: string;
  admin: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
}

// Admin user interface
interface AdminUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: "admin";
  createdAt: string;
  updatedAt: string;
  tokenVersion?: number;
}

interface AdminCreateRequest {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

interface AdminUpdateRequest {
  firstName?: string;
  lastName?: string;
  email?: string;
}

interface AdminService {
  // Plan management
  listPlans: () => Promise<AdminPlan[]>;
  createPlan: (data: AdminCreatePlanRequest) => Promise<AdminPlan>;
  updatePlan: (
    id: string,
    data: Partial<AdminCreatePlanRequest>,
  ) => Promise<AdminPlan>;
  deletePlan: (id: string) => Promise<{ message: string }>;

  // Features management
  getAvailableFeatures: () => Promise<string[]>;

  // Admin management
  listAdmins: () => Promise<AdminUser[]>;
  createAdmin: (data: AdminCreateRequest) => Promise<AdminUser>;
  updateAdmin: (id: string, data: AdminUpdateRequest) => Promise<AdminUser>;
  deleteAdmin: (id: string) => Promise<{ message: string }>;

  // User management
  getUsers: () => Promise<PlatformUser[]>;
  deleteUser: (id: string) => Promise<{
    message: string;
    data: { id: string; email: string; firstName: string; lastName: string };
  }>;

  // Dashboard analytics
  getDashboardStats: () => Promise<DashboardStats>;
  getUsageMetrics: (timeRange?: string) => Promise<UsageMetrics>;

  // System health
  getSystemHealth: () => Promise<PlatformHealth>;

  // Setup and initialization methods
  checkAdminExists: () => Promise<{ hasAdmins: boolean; message: string }>;
  createFirstAdmin: (adminData: FirstAdminData) => Promise<FirstAdminResponse>;
  requestFirstAdminOTP: (adminData: FirstAdminData) => Promise<{
    success: boolean;
    message: string;
    otp?: string;
  }>;
  verifyFirstAdminOTP: (
    email: string,
    otp: string,
  ) => Promise<FirstAdminResponse>;
  requestPasswordReset: (email: string) => Promise<{
    success: boolean;
    message: string;
    otp?: string;
  }>;
  verifyPasswordResetOTP: (data: {
    email: string;
    otp: string;
    newPassword: string;
  }) => Promise<{ success: boolean; message: string; admin?: AdminUser }>;
  resetAdminPassword: (
    adminId: string,
    newPassword: string,
  ) => Promise<{ success: boolean; message: string; admin?: AdminUser }>;
  verifyCurrentPassword: (
    currentPassword: string,
  ) => Promise<{ success: boolean; message: string; valid?: boolean }>;
}

// Admin API base URL
const ADMIN_API_BASE = `${import.meta.env.VITE_API_BASE_URL}/api/admin`;

// Session expiration handler for admin
let sessionExpiredHandled = false;

const handleAdminSessionExpired = () => {
  if (sessionExpiredHandled) {
    return; // Prevent multiple toasts and redirects
  }

  sessionExpiredHandled = true;

  // Clear any admin-related local storage or state
  localStorage.removeItem("admin_session");

  // Show error toast
  toast.error("Session administrateur expirée. Redirection en cours...");

  // Redirect to admin login page after a short delay
  setTimeout(() => {
    // Reset the flag before redirect so future sessions can handle expiration
    sessionExpiredHandled = false;
    window.location.href = "/admin";
  }, 1000);
};

// Enhanced fetch function with session handling for admin
const adminFetch = async (url: string, options: RequestInit = {}) => {
  const response = await fetch(url, {
    credentials: "include",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

  if (response.status === 401) {
    // Session expired
    handleAdminSessionExpired();
    throw new Error("Session administrateur expirée");
  }

  return response;
};

// Admin service implementation
const adminService: AdminService = {
  // Setup and initialization methods
  checkAdminExists: async (): Promise<{
    hasAdmins: boolean;
    message: string;
  }> => {
    try {
      const response = await fetch(`${ADMIN_API_BASE}/check-admin-exists`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to check admin existence");
      }

      const result: { success: boolean; hasAdmins: boolean; message: string } =
        await response.json();
      return {
        hasAdmins: result.hasAdmins,
        message: result.message,
      };
    } catch (error) {
      console.error("Error checking admin existence:", error);
      throw new Error("Failed to check admin existence");
    }
  },

  requestFirstAdminOTP: async (
    adminData: FirstAdminData,
  ): Promise<{
    success: boolean;
    message: string;
    otp?: string;
  }> => {
    try {
      const response = await fetch(
        `${ADMIN_API_BASE}/request-first-admin-otp`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            firstName: adminData.firstName,
            lastName: adminData.lastName,
            email: adminData.email,
            password: adminData.password,
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Failed to request first admin OTP",
        );
      }

      const result: { success: boolean; message: string; otp?: string } =
        await response.json();
      return result;
    } catch (error) {
      console.error("Error requesting first admin OTP:", error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error("Failed to request first admin OTP");
    }
  },

  verifyFirstAdminOTP: async (
    email: string,
    otp: string,
  ): Promise<FirstAdminResponse> => {
    try {
      const response = await fetch(`${ADMIN_API_BASE}/verify-first-admin-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          otp,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Failed to verify first admin OTP",
        );
      }

      const result: {
        success: boolean;
        message: string;
        admin: FirstAdminResponse["admin"];
      } = await response.json();
      return {
        message: result.message,
        admin: result.admin,
      };
    } catch (error) {
      console.error("Error verifying first admin OTP:", error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error("Failed to verify first admin OTP");
    }
  },

  createFirstAdmin: async (
    adminData: FirstAdminData,
  ): Promise<FirstAdminResponse> => {
    try {
      const response = await fetch(`${ADMIN_API_BASE}/create-first-admin`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(adminData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to create first admin");
      }

      const result: {
        success: boolean;
        message: string;
        admin: FirstAdminResponse["admin"];
      } = await response.json();
      return {
        message: result.message,
        admin: result.admin,
      };
    } catch (error) {
      console.error("Error creating first admin:", error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error("Failed to create first admin");
    }
  },

  getDashboardStats: async (): Promise<DashboardStats> => {
    try {
      const response = await adminFetch(`${ADMIN_API_BASE}/dashboard/stats`, {
        method: "GET",
      });

      if (!response.ok) {
        const error = await response.text();
        console.error("Failed to fetch dashboard stats:", error);
        throw new Error(
          "Échec de la récupération des statistiques du tableau de bord",
        );
      }

      const result: { data: DashboardStats } = await response.json();
      return result.data;
    } catch (error) {
      console.error("Erreur lors de la récupération des statistiques:", error);
      throw new Error(
        "Échec de la récupération des statistiques du tableau de bord",
      );
    }
  },

  getUsageMetrics: async (timeRange = "30d"): Promise<UsageMetrics> => {
    try {
      const response = await adminFetch(
        `${ADMIN_API_BASE}/dashboard/metrics?timeRange=${timeRange}`,
        {
          method: "GET",
        },
      );

      if (!response.ok) {
        const error = await response.text();
        console.error("Failed to fetch usage metrics:", error);
        throw new Error("Échec de la récupération des métriques d'utilisation");
      }

      const result: { data: UsageMetrics } = await response.json();
      return result.data;
    } catch (error) {
      console.error("Erreur lors de la récupération des métriques:", error);
      throw new Error("Échec de la récupération des métriques d'utilisation");
    }
  },

  // User management
  getUsers: async (): Promise<PlatformUser[]> => {
    try {
      const response = await adminFetch(`${ADMIN_API_BASE}/users`, {
        method: "GET",
      });

      if (!response.ok) {
        const error = await response.text();
        console.error("Failed to fetch users:", error);
        throw new Error("Échec de la récupération des utilisateurs");
      }

      const data: { data?: PlatformUser[] } = await response.json();
      return data.data || [];
    } catch (error) {
      console.error("Erreur lors de la récupération des utilisateurs:", error);
      throw new Error("Échec de la récupération des utilisateurs");
    }
  },

  deleteUser: async (
    id: string,
  ): Promise<{
    message: string;
    data: { id: string; email: string; firstName: string; lastName: string };
  }> => {
    try {
      const response = await adminFetch(`${ADMIN_API_BASE}/users/${id}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        const error = await response.text();
        console.error("Failed to delete user:", error);
        throw new Error("Échec de la suppression de l'utilisateur");
      }

      const result: {
        message: string;
        data: {
          id: string;
          email: string;
          firstName: string;
          lastName: string;
        };
      } = await response.json();
      return result;
    } catch (error) {
      console.error("Erreur lors de la suppression de l'utilisateur:", error);
      throw new Error("Échec de la suppression de l'utilisateur");
    }
  },

  // System health monitoring
  getSystemHealth: async (): Promise<PlatformHealth> => {
    const healthResults: PlatformHealth = {
      apiStatus: {
        status: "down",
        responseTime: 0,
        lastChecked: new Date().toISOString(),
      },
      database: {
        status: "disconnected",
        responseTime: 0,
        lastChecked: new Date().toISOString(),
      },
      emailService: {
        status: "inactive",
        responseTime: 0,
        lastChecked: new Date().toISOString(),
      },
      stripeApi: {
        status: "inactive",
        responseTime: 0,
        lastChecked: new Date().toISOString(),
      },
    };

    try {
      // Check API Health
      const apiStartTime = Date.now();
      const healthResponse = await adminFetch(`${ADMIN_API_BASE}/health`, {
        method: "GET",
      });
      const apiResponseTime = Date.now() - apiStartTime;

      if (healthResponse.ok) {
        const healthData = await healthResponse.json();

        healthResults.apiStatus = {
          status: "operational",
          responseTime: apiResponseTime,
          lastChecked: new Date().toISOString(),
        };

        // Map backend response structure: data.api, data.database
        const backendHealth = healthData.data || healthData;

        // Database status from health endpoint
        if (backendHealth.database) {
          healthResults.database = {
            status:
              backendHealth.database.status === "healthy"
                ? "connected"
                : "error",
            responseTime: backendHealth.database.responseTime || 0,
            lastChecked: new Date().toISOString(),
          };
        }

        // Email service status from health endpoint
        if (backendHealth.emailService) {
          healthResults.emailService = {
            status:
              backendHealth.emailService.status === "healthy"
                ? "active"
                : backendHealth.emailService.status === "degraded"
                  ? "degraded"
                  : "error",
            responseTime: backendHealth.emailService.responseTime || 0,
            lastChecked: new Date().toISOString(),
          };
        }

        // Stripe API status from health endpoint
        if (backendHealth.stripeApi) {
          healthResults.stripeApi = {
            status:
              backendHealth.stripeApi.status === "healthy"
                ? "active"
                : backendHealth.stripeApi.status === "degraded"
                  ? "degraded"
                  : "error",
            responseTime: backendHealth.stripeApi.responseTime || 0,
            lastChecked: new Date().toISOString(),
          };
        }
      } else {
        healthResults.apiStatus = {
          status: healthResponse.status >= 500 ? "down" : "degraded",
          responseTime: apiResponseTime,
          lastChecked: new Date().toISOString(),
        };
      }
    } catch (error) {
      console.error(
        "Erreur lors de la vérification de la santé du système:",
        error,
      );

      // Try individual checks if general health endpoint fails
      try {
        // Basic API connectivity check
        const apiStartTime = Date.now();
        const pingResponse = await adminFetch(
          `${ADMIN_API_BASE}/dashboard/stats`,
          {
            method: "GET",
          },
        );
        const apiResponseTime = Date.now() - apiStartTime;

        if (pingResponse.ok) {
          healthResults.apiStatus = {
            status: "operational",
            responseTime: apiResponseTime,
            lastChecked: new Date().toISOString(),
          };

          // If API is working, assume database is connected (since we can fetch stats)
          healthResults.database = {
            status: "connected",
            responseTime: apiResponseTime,
            lastChecked: new Date().toISOString(),
          };
        }
      } catch (pingError) {
        console.error("API ping failed:", pingError);
      }
    }

    return healthResults;
  },

  // Plan management methods using admin endpoints
  listPlans: async (): Promise<AdminPlan[]> => {
    try {
      const response = await adminFetch(`${ADMIN_API_BASE}/plans`, {
        method: "GET",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: { data?: AdminPlan[] } = await response.json();
      return data.data || [];
    } catch (error) {
      console.error("Error listing plans:", error);
      throw error;
    }
  },

  createPlan: async (data: AdminCreatePlanRequest): Promise<AdminPlan> => {
    try {
      const response = await adminFetch(`${ADMIN_API_BASE}/plans`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: { data: AdminPlan } = await response.json();
      return result.data;
    } catch (error) {
      console.error("Error creating plan:", error);
      throw error;
    }
  },

  updatePlan: async (
    id: string,
    data: Partial<AdminCreatePlanRequest>,
  ): Promise<AdminPlan> => {
    try {
      const response = await adminFetch(`${ADMIN_API_BASE}/plans/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: { data: AdminPlan } = await response.json();
      return result.data;
    } catch (error) {
      console.error("Error updating plan:", error);
      throw error;
    }
  },

  deletePlan: async (id: string): Promise<{ message: string }> => {
    try {
      const response = await adminFetch(`${ADMIN_API_BASE}/plans/${id}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        const error = await response.text();
        console.error("Failed to delete plan:", error);
        throw new Error("Échec de la suppression du plan");
      }

      const result: { message: string } = await response.json();
      return { message: result.message || "Plan supprimé avec succès" };
    } catch (error) {
      console.error("Erreur lors de la suppression du plan:", error);
      throw new Error("Échec de la suppression du plan");
    }
  },

  // Features management
  getAvailableFeatures: async (): Promise<string[]> => {
    try {
      const response = await adminFetch(`${ADMIN_API_BASE}/features`, {
        method: "GET",
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch features");
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error("Error fetching features:", error);
      throw error;
    }
  },

  // Admin management methods
  listAdmins: async (): Promise<AdminUser[]> => {
    try {
      const response = await adminFetch(`${ADMIN_API_BASE}/admins`, {
        method: "GET",
      });

      if (!response.ok) {
        const error = await response.text();
        console.error("Failed to fetch admins:", error);
        throw new Error("Échec de la récupération des administrateurs");
      }

      const data: { data?: AdminUser[] } = await response.json();
      return data.data || [];
    } catch (error) {
      console.error("Erreur lors de la récupération des admins:", error);
      throw new Error("Échec de la récupération des administrateurs");
    }
  },

  createAdmin: async (data: AdminCreateRequest): Promise<AdminUser> => {
    try {
      const response = await adminFetch(`${ADMIN_API_BASE}/admins`, {
        method: "POST",
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response.text();
        console.error("Failed to create admin:", error);
        throw new Error("Échec de la création de l'administrateur");
      }

      const result: { data: AdminUser } = await response.json();
      return result.data;
    } catch (error) {
      console.error("Erreur lors de la création de l'admin:", error);
      throw new Error("Échec de la création de l'administrateur");
    }
  },

  updateAdmin: async (
    id: string,
    data: AdminUpdateRequest,
  ): Promise<AdminUser> => {
    try {
      const response = await adminFetch(`${ADMIN_API_BASE}/admins/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response.text();
        console.error("Failed to update admin:", error);
        throw new Error("Échec de la mise à jour de l'administrateur");
      }

      const result: { data: AdminUser } = await response.json();
      return result.data;
    } catch (error) {
      console.error("Erreur lors de la mise à jour de l'admin:", error);
      throw new Error("Échec de la mise à jour de l'administrateur");
    }
  },

  deleteAdmin: async (id: string): Promise<{ message: string }> => {
    try {
      const response = await adminFetch(`${ADMIN_API_BASE}/admins/${id}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        const error = await response.text();
        console.error("Failed to delete admin:", error);
        throw new Error("Échec de la suppression de l'administrateur");
      }

      const result: { message: string } = await response.json();
      return {
        message: result.message || "Administrateur supprimé avec succès",
      };
    } catch (error) {
      console.error("Erreur lors de la suppression de l'admin:", error);
      throw new Error("Échec de la suppression de l'administrateur");
    }
  },

  // Admin Password Reset
  requestPasswordReset: async (
    email: string,
  ): Promise<{
    success: boolean;
    message: string;
    otp?: string;
  }> => {
    try {
      const response = await fetch(`${ADMIN_API_BASE}/request-password-reset`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Failed to request password reset",
        );
      }

      const result: {
        success: boolean;
        message: string;
        otp?: string;
      } = await response.json();
      return result;
    } catch (error) {
      console.error("Error requesting password reset:", error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error("Failed to request password reset");
    }
  },

  verifyPasswordResetOTP: async (data: {
    email: string;
    otp: string;
    newPassword: string;
  }): Promise<{
    success: boolean;
    message: string;
    admin?: AdminUser;
  }> => {
    try {
      const response = await fetch(`${ADMIN_API_BASE}/verify-password-reset`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Failed to verify password reset OTP",
        );
      }

      const result: {
        success: boolean;
        message: string;
        data?: any;
      } = await response.json();
      return {
        success: result.success,
        message: result.message,
        admin: result.data,
      };
    } catch (error) {
      console.error("Error verifying password reset OTP:", error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error("Failed to verify password reset OTP");
    }
  },

  // Admin Password Reset (Admin-initiated)
  resetAdminPassword: async (
    adminId: string,
    newPassword: string,
  ): Promise<{
    success: boolean;
    message: string;
    admin?: AdminUser;
  }> => {
    try {
      const response = await fetch(
        `${ADMIN_API_BASE}/admins/${adminId}/reset-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({ newPassword }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to reset admin password");
      }

      const result: {
        success: boolean;
        message: string;
        data?: any;
      } = await response.json();
      return {
        success: result.success,
        message: result.message,
        admin: result.data,
      };
    } catch (error) {
      console.error("Error resetting admin password:", error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error("Failed to reset admin password");
    }
  },

  // Legal Content Management
  // Legal content methods removed - not needed in generic SaaS template

  // Verify current password before reset
  verifyCurrentPassword: async (
    currentPassword: string,
  ): Promise<{
    success: boolean;
    message: string;
    valid?: boolean;
  }> => {
    try {
      const response = await fetch(
        `${ADMIN_API_BASE}/verify-current-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({ currentPassword }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Failed to verify current password",
        );
      }

      const result: {
        success: boolean;
        message: string;
        valid?: boolean;
      } = await response.json();
      return {
        success: result.success,
        message: result.message,
        valid: result.valid,
      };
    } catch (error) {
      console.error("Error verifying current password:", error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error("Failed to verify current password");
    }
  },
};

// Reset session expiration handler (call this when a new admin session starts)
export const resetAdminSessionHandler = () => {
  sessionExpiredHandled = false;
};

export default adminService;
export type {
  DashboardStats,
  UsageMetrics,
  AdminService,
  PlatformUser,
  PlatformHealth,
  FirstAdminData,
  FirstAdminResponse,
  AdminUser,
  AdminCreateRequest,
  AdminUpdateRequest,
};
